// The server's session claims, and the only path by which they enter the UI.
//
// ─── Why this module exists ───────────────────────────────────────────────
//
// The console has every ingredient needed to FABRICATE an authority display.
// It knows the connected wallet address, it knows `REQUIRED_CHAIN_ID` from its
// own config, and a vault address is a public constant it could equally have
// hard-coded. A header rendered from those three would look exactly like a
// correct one and would mean nothing at all: it would say "you are an owner of
// vault X" on the strength of the browser's own opinion.
//
// So the rule for Phase 1 is that authority is DISPLAYED ONLY as the server
// reported it. `parseServerSession` is the single entry point, it reads one
// object — the body of `GET /v2/admin/bff/session` — and it has no access to
// config, no access to the wallet, and no defaults. A field the server did not
// send comes back `null` and the UI renders an em dash, which is the honest
// answer to "which vault?" when the server did not say.
//
// That is also why every field is type-checked rather than cast. The body is
// already trusted (it arrives over TLS on an authenticated call), but a cast
// would let a SHAPE change — a renamed column, a projection that silently
// stopped emitting `authorityVault` — surface as `undefined` rendered into the
// page rather than as a missing value the UI can mark.
//
// ─── What is deliberately absent ──────────────────────────────────────────
//
// No query-string reader, no `localStorage` reader, no form input, no config
// import. There is nowhere in this file for a client-asserted authority to
// enter, which is the property §5 asks for and the reason the parse lives
// apart from the session store rather than inside it.

import type { SessionAuthority } from "./session";
import { BASE_SEPOLIA_CHAIN_ID } from "./wallet";

/** The public-safe session projection, as `admin-bff-session.ts` emits it. */
export interface ServerSessionClaims {
  sessionId: string | null;
  ownerSubject: string | null;
  walletAddress: string | null;
  deviceId: string | null;
  expiresAt: string | null;
  authority: SessionAuthority;
}

function str(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Parse the body of `GET /v2/admin/bff/session`.
 *
 * Returns `null` when the body is not a session envelope at all — a 200 with
 * an unexpected shape must not be mistaken for a session with empty claims.
 */
export function parseServerSession(body: unknown): ServerSessionClaims | null {
  if (body === null || typeof body !== "object") return null;
  const s = (body as { session?: unknown }).session;
  if (s === null || typeof s !== "object") return null;
  const o = s as Record<string, unknown>;
  return {
    sessionId: str(o.sessionId),
    ownerSubject: str(o.ownerSubject),
    walletAddress: str(o.walletAddress),
    deviceId: str(o.deviceId),
    expiresAt: str(o.expiresAt),
    authority: {
      deviceId: str(o.deviceId),
      signerAddress: str(o.signerAddress),
      authorityVault: str(o.authorityVault),
      authorityChainId: num(o.authorityChainId),
      membership: str(o.membership),
      authVersion: num(o.authVersion),
    },
  };
}

/**
 * Re-validate an authority block restored from `sessionStorage`.
 *
 * The stored copy is a CACHE of what the server said, so that the header can
 * render on reload without a round trip. `sessionStorage` is writable by
 * whoever holds the browser, so a hand-edited value could otherwise be cast
 * straight into the UI — and while it grants nothing (the server re-reads the
 * row and re-proves membership on every authority-sensitive call), a console
 * that renders `authorityChainId: "yes"` or a membership of `{}` is lying about
 * where its numbers came from.
 *
 * Running the restored copy through the same field checks as a fresh server
 * response means the display is always a well-formed projection or an em dash.
 */
export function normalizeAuthority(raw: unknown): SessionAuthority | null {
  if (raw === null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    signerAddress: str(o.signerAddress),
    authorityVault: str(o.authorityVault),
    authorityChainId: num(o.authorityChainId),
    membership: str(o.membership),
    authVersion: num(o.authVersion),
    deviceId: str(o.deviceId),
  };
}

// ─── Presentation ─────────────────────────────────────────────────────────
//
// Pure formatters over values the server supplied. None of them substitutes a
// default: an absent value formats as "—", never as the console's own guess.

/** `0xabcdef01…89abcdef` → `0xabcd…cdef`. Pure formatting; no address is special. */
export function shortAddress(a: string | null): string {
  if (a === null) return "—";
  return a.length <= 12 ? a : `${a.slice(0, 6)}…${a.slice(-4)}`;
}

/** `owner` → `OWNER`. Rendered upper-case because it is a state, not prose. */
export function membershipLabel(m: string | null): string {
  return m === null ? "—" : m.toUpperCase();
}

/** A chain id the server minted the session under. Named only when it is the
 *  chain this console is built for; any other id is shown as the bare number,
 *  because naming a chain we did not expect would be a guess. */
export function chainLabel(id: number | null): string {
  if (id === null) return "—";
  return id === BASE_SEPOLIA_CHAIN_ID ? `Base Sepolia · ${id}` : `Chain ${id}`;
}

/** True when the session carries a vault-bound (Phase 1) authority.
 *  `auth_version` 1 rows are legacy owner-wallet sessions minted before the
 *  cutover; they are still valid sessions, and saying so is the point. */
export function isVaultBound(a: SessionAuthority | null | undefined): boolean {
  return a?.authVersion !== null && a?.authVersion !== undefined && a.authVersion >= 2;
}
